import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  useCreateBlockNote,
  FormattingToolbar,
  FormattingToolbarController,
  getFormattingToolbarItems,
  useComponentsContext,
  useBlockNoteEditor,
  useEditorSelectionChange,
} from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import '@blocknote/core/fonts/inter.css';
import '@blocknote/mantine/style.css';
import { useTheme } from '../../context/ThemeContext';
import {
  getVision,
  getResource,
  updateResource,
  deleteResource,
  draftResourceBody,
} from '../../actions/planningAction';
import { flattenNodes, LinkPicker } from './PlanningLibrary';
import './PlanningDocEditor.css';

// Standard ProseMirror algorithm: find the full range of a mark around a position.
function getMarkRangeAtPos($pos, markType) {
  const after = $pos.parent.childAfter($pos.parentOffset);
  if (!after.node) return null;
  const mark = after.node.marks.find(m => m.type === markType);
  if (!mark) return null;
  let startIndex = $pos.index();
  let startPos = $pos.start() + after.offset;
  let endIndex = startIndex + 1;
  let endPos = startPos + after.node.nodeSize;
  while (startIndex > 0 && mark.isInSet($pos.parent.child(startIndex - 1).marks)) {
    startIndex -= 1;
    startPos -= $pos.parent.child(startIndex).nodeSize;
  }
  while (endIndex < $pos.parent.childCount && mark.isInSet($pos.parent.child(endIndex).marks)) {
    endPos += $pos.parent.child(endIndex).nodeSize;
    endIndex += 1;
  }
  return { from: startPos, to: endPos };
}

// "Remove link" toolbar button — only shows when the selection sits on a link.
function RemoveLinkButton() {
  const editor = useBlockNoteEditor();
  const Components = useComponentsContext();
  const [hasLink, setHasLink] = useState(false);

  useEditorSelectionChange(() => {
    setHasLink(!!editor.getSelectedLinkUrl());
  }, editor);

  if (!hasLink) return null;

  const removeLink = () => {
    editor.transact((tr) => {
      const linkType = editor.pmSchema.marks.link;
      if (!linkType) return;
      let { from, to, empty, $from } = tr.selection;
      if (empty) {
        const range = getMarkRangeAtPos($from, linkType);
        if (!range) return;
        from = range.from;
        to = range.to;
      }
      tr.removeMark(from, to, linkType);
    });
    setHasLink(false);
  };

  return (
    <Components.FormattingToolbar.Button
      mainTooltip="Remove link"
      label="Unlink"
      onClick={removeLink}
    />
  );
}

export default function PlanningDocEditor() {
  const { visionId, resourceId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { theme } = useTheme();
  const { milestones } = useSelector(s => s.planning);

  const [resource, setResource] = useState(null);
  const [title, setTitle] = useState('');
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(true);
  const [showPicker, setShowPicker] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [assistOpen, setAssistOpen] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [docHasContent, setDocHasContent] = useState(false);

  const editor = useCreateBlockNote();
  const saveTimer = useRef(null);
  const latest = useRef({ title: '', links: [] });
  const loadedRef = useRef(false);
  latest.current = { title, links };

  const allNodes = useMemo(() => flattenNodes(milestones), [milestones]);
  const nodeById = useMemo(() => Object.fromEntries(allNodes.map(n => [n._id, n])), [allNodes]);

  // Load resource + ensure milestones present for the link picker
  useEffect(() => {
    if (!milestones || milestones.length === 0) dispatch(getVision(visionId)).catch(() => {});
    setLoading(true);
    dispatch(getResource(resourceId))
      .then(async (d) => {
        setResource(d.resource);
        setTitle(d.resource.title || '');
        setLinks((d.resource.linkedNodeIds || []).map(String));
        // Seed the editor from the stored markdown
        const md = d.resource.body || '';
        setDocHasContent(!!md.trim());
        if (md.trim()) {
          const blocks = await editor.tryParseMarkdownToBlocks(md);
          editor.replaceBlocks(editor.document, blocks);
          const last = editor.document[editor.document.length - 1];
          if (last && last.type === 'table') editor.insertBlocks([{ type: 'paragraph' }], last, 'after');
        }
        loadedRef.current = true;
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [resourceId, visionId, dispatch]); // eslint-disable-line

  const persist = useCallback(async () => {
    const { title: t, links: l } = latest.current;
    let md = '';
    try { md = await editor.blocksToMarkdownLossy(editor.document); } catch (_) {}
    try {
      await dispatch(updateResource(resourceId, { title: t, body: md, linkedNodeIds: l }));
      setSaved(true);
    } catch (_) {}
  }, [dispatch, resourceId, editor]);

  const queueSave = useCallback(() => {
    setSaved(false);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(persist, 900);
  }, [persist]);

  useEffect(() => () => { clearTimeout(saveTimer.current); if (loadedRef.current) persist(); }, []); // eslint-disable-line

  // Tables can become the terminal block, leaving nowhere to type after them.
  // Guarantee a trailing empty paragraph so the user can always escape a table.
  const ensureTrailingBlock = useCallback(() => {
    const blocks = editor.document;
    const last = blocks[blocks.length - 1];
    if (last && last.type === 'table') {
      editor.insertBlocks([{ type: 'paragraph' }], last, 'after');
    }
  }, [editor]);

  const onEditorChange = () => {
    if (!loadedRef.current) return;
    ensureTrailingBlock();
    queueSave();
  };

  const toggleLink = (id) => {
    setLinks(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    setTimeout(queueSave, 0);
  };

  const remove = async () => {
    clearTimeout(saveTimer.current);
    try { await dispatch(deleteResource(resourceId)); } catch (_) {}
    navigate(`/dashboard/planning/${visionId}`);
  };

  const openAssist = () => {
    if (links.length === 0) {
      window.alert('Link at least one node first — Dmyor Assist writes from the linked nodes.');
      return;
    }
    setAssistOpen(v => !v);
  };

  const runAssist = async () => {
    if (generating) return;
    if (docHasContent && !instructions.trim()) {
      window.alert('Tell Dmyor Assist what to change.');
      return;
    }
    setGenerating(true);
    setAssistOpen(false);
    try {
      // Make sure the latest links are saved before the server reads them
      await persist();
      const d = await dispatch(draftResourceBody(resourceId, instructions));
      const md = d.body || '';
      if (md.trim()) {
        const blocks = await editor.tryParseMarkdownToBlocks(md);
        editor.replaceBlocks(editor.document, blocks);
        const last = editor.document[editor.document.length - 1];
        if (last && last.type === 'table') editor.insertBlocks([{ type: 'paragraph' }], last, 'after');
      }
      // Adopt the generated title when the doc has no meaningful title yet
      if (d.title && (!title.trim() || title.trim().toLowerCase() === 'untitled')) {
        latest.current = { ...latest.current, title: d.title }; // so persist saves it
        setTitle(d.title);
      }
      setInstructions('');
      setDocHasContent(true);
      await persist();
    } catch (err) {
      window.alert(err?.response?.data?.message || 'Could not generate. Try again.');
    }
    setGenerating(false);
  };

  const goBack = () => { clearTimeout(saveTimer.current); if (loadedRef.current) persist(); navigate(-1); };

  return (
    <div className={`pde-wrap${theme === 'light' ? ' pde-wrap--light' : ''}`}>
      {/* Top bar */}
      <div className="pde-topbar">
        <button className="pde-back" onClick={goBack}>← Back</button>
        <span className="pde-save-state">{loading ? '' : saved ? 'Saved' : 'Saving…'}</span>
        <div className="pde-assist-wrap">
          <button
            className="pde-generate"
            onClick={openAssist}
            disabled={generating || loading}
            title="Let Dmyor Assist write this document from its linked nodes"
          >
            {generating ? '✦ Dmyor Assist is writing…' : '✦ Dmyor Assist'}
          </button>
          {assistOpen && (
            <div className="pde-assist-pop">
              <div className="pde-assist-head">✦ Dmyor Assist</div>
              <p className="pde-assist-note">
                {docHasContent
                  ? 'What would you like to change? It edits just that and keeps the rest of your document.'
                  : `It reads your linked node${links.length > 1 ? 's' : ''} and their place in the plan. Add anything specific you want (optional).`}
              </p>
              <textarea
                className="pde-assist-input"
                placeholder={docHasContent
                  ? 'e.g. add a rest day on Thursday, make week 3 heavier, add a warm-up section…'
                  : 'e.g. focus on a 4-day split, keep it beginner-friendly, include a grocery list…'}
                value={instructions}
                onChange={e => setInstructions(e.target.value)}
                rows={3}
                autoFocus
              />
              <div className="pde-assist-actions">
                <button className="pde-assist-cancel" onClick={() => setAssistOpen(false)}>Cancel</button>
                <button className="pde-assist-go" onClick={runAssist}>
                  {docHasContent ? '✦ Apply changes' : '✦ Write it'}
                </button>
              </div>
            </div>
          )}
        </div>
        <button className="pde-del" onClick={() => setConfirmDelete(true)} title="Delete document">Delete</button>
      </div>

      {confirmDelete && (
        <div className="pde-confirm-overlay" onMouseDown={() => setConfirmDelete(false)}>
          <div className="pde-confirm" onMouseDown={e => e.stopPropagation()}>
            <h3 className="pde-confirm-title">Delete this document?</h3>
            <p className="pde-confirm-text">
              "{title || 'Untitled'}" will be permanently removed. This cannot be undone.
            </p>
            <div className="pde-confirm-actions">
              <button className="pde-confirm-cancel" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button className="pde-confirm-go" onClick={remove}>Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className="pde-inner">
        {/* Title */}
        <input
          className="pde-title"
          value={title}
          placeholder="Untitled document"
          onChange={e => { setTitle(e.target.value); queueSave(); }}
        />

        {/* Linked nodes */}
        <div className="pde-links">
          {links.map(id => {
            const n = nodeById[id];
            return (
              <span key={id} className="pde-chip">
                <span className={`pde-dot pde-dot--${n?.type || 'node'}`} />
                {n ? (n.content.length > 40 ? n.content.slice(0, 40) + '…' : n.content) : 'node'}
                <button className="pde-chip-x" onClick={() => toggleLink(id)}>×</button>
              </span>
            );
          })}
          <div className="pde-chip-add-wrap">
            <button className="pde-chip-add" onClick={() => setShowPicker(v => !v)}>+ Link node</button>
            {showPicker && (
              <LinkPicker allNodes={allNodes} selected={links} onToggle={toggleLink} onClose={() => setShowPicker(false)} />
            )}
          </div>
        </div>

        {/* WYSIWYG editor */}
        <div className="pde-editor-shell">
          {loading && (
            <div className="pde-thinking">
              <div className="pde-thinking-card">
                <span className="pde-doc-spinner" />
                <div className="pde-thinking-sub">Loading document…</div>
              </div>
            </div>
          )}
          {generating && (
            <div className="pde-thinking">
              <div className="pde-thinking-card">
                <span className="pde-thinking-spark">✦</span>
                <div className="pde-thinking-text">
                  Dmyor Assist is {docHasContent ? 'revising' : 'writing'}
                  <span className="pde-thinking-dots"><span>.</span><span>.</span><span>.</span></span>
                </div>
                <div className="pde-thinking-sub">Reading your linked nodes and the plan around them</div>
              </div>
            </div>
          )}
          <BlockNoteView
            editor={editor}
            theme={theme === 'light' ? 'light' : 'dark'}
            onChange={onEditorChange}
            formattingToolbar={false}
          >
            <FormattingToolbarController
              formattingToolbar={() => (
                <FormattingToolbar>
                  {getFormattingToolbarItems()}
                  <RemoveLinkButton key="removeLink" />
                </FormattingToolbar>
              )}
            />
          </BlockNoteView>
        </div>
      </div>
    </div>
  );
}
